import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Plus,
  NotebookPen,
  Loader2,
  ChevronDown,
  ChevronUp,
  Trash2,
  Sparkles,
  Users,
  Calendar,
  X,
} from "lucide-react";
import type { Stakeholder } from "@/lib/projectStore";

const BASE = process.env.REACT_APP_BACKEND_URL;

interface Props {
  baProjectId: string | null;
  stakeholders?: Stakeholder[];
}

interface ExtractedItem {
  type: "requirement" | "action" | "decision" | "risk";
  text: string;
}

interface MeetingNote {
  id: string;
  title: string;
  meeting_date: string;
  attendees: string;
  raw_notes: string;
  extracted_items?: ExtractedItem[];
  created_at: string;
}

const ITEM_TYPE_COLORS: Record<string, string> = {
  requirement: "bg-blue-100 text-blue-800 border-blue-300",
  action: "bg-amber-100 text-amber-800 border-amber-300",
  decision: "bg-purple-100 text-purple-800 border-purple-300",
  risk: "bg-red-100 text-red-800 border-red-300",
};

const ITEM_TYPE_LABELS: Record<string, string> = {
  requirement: "Requirement",
  action: "Action",
  decision: "Decision",
  risk: "Risk",
};

const EMPTY_FORM = {
  title: "",
  meeting_date: "",
  attendees: "",
  raw_notes: "",
};

export default function MeetingNotesPanel({ baProjectId, stakeholders = [] }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const [notes, setNotes] = useState<MeetingNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [extracting, setExtracting] = useState<Record<string, boolean>>({});
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // helpers to resolve stored attendees (IDs or legacy free-text)
  const resolveAttendees = (raw: string): string => {
    if (!raw) return "";
    const parts = raw.split(",").map(s => s.trim()).filter(Boolean);
    return parts.map(p => {
      const found = stakeholders.find(s => s.id === p);
      return found ? `${found.name} (${found.role})` : p;
    }).join(", ");
  };

  const toggleAttendee = (id: string) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  useEffect(() => {
    if (baProjectId) load();
    else setNotes([]);
  }, [baProjectId]);

  const load = async () => {
    if (!baProjectId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE}/api/tools/meeting-notes?project_id=${baProjectId}`,
        { headers: authHeader }
      );
      setNotes(res.data || []);
    } catch (e) {
      console.error("Failed to load meeting notes", e);
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const attendees = selectedIds.length > 0
        ? selectedIds.join(",")
        : form.attendees;
      await axios.post(
        `${BASE}/api/tools/meeting-notes`,
        { ...form, attendees, project_id: baProjectId },
        { headers: authHeader }
      );
      setShowForm(false);
      setForm({ ...EMPTY_FORM });
      setSelectedIds([]);
      load();
    } catch (e) {
      console.error("Failed to save meeting note", e);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await axios.delete(`${BASE}/api/tools/meeting-notes/${id}`, {
        headers: authHeader,
      });
      setDeleteConfirmId(null);
      if (expandedId === id) setExpandedId(null);
      load();
    } catch (e) {
      console.error("Failed to delete meeting note", e);
    }
  };

  const extract = async (id: string) => {
    setExtracting((prev) => ({ ...prev, [id]: true }));
    try {
      const res = await axios.post(
        `${BASE}/api/tools/meeting-notes/${id}/extract`,
        {},
        { headers: authHeader }
      );
      const items: ExtractedItem[] = res.data?.items || [];
      setNotes((prev) =>
        prev.map((n) => (n.id === id ? { ...n, extracted_items: items } : n))
      );
    } catch (e) {
      console.error("Failed to extract items", e);
    } finally {
      setExtracting((prev) => ({ ...prev, [id]: false }));
    }
  };

  const groupedItems = (items: ExtractedItem[]) => {
    const groups: Record<string, ExtractedItem[]> = {};
    items.forEach((item) => {
      if (!groups[item.type]) groups[item.type] = [];
      groups[item.type].push(item);
    });
    return groups;
  };

  if (!baProjectId) {
    return (
      <div className="h-full flex items-center justify-center text-[#5C5C57]">
        <p className="text-sm">No project selected.</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <NotebookPen className="h-5 w-5 text-[#314E6E]" />
          <h2 className="text-lg font-semibold text-[#1E1E1C]">Meeting Notes</h2>
          <span className="text-xs text-[#5C5C57] bg-[#F7F4EE] border border-[#D9D4CC] rounded-full px-2 py-0.5">
            {notes.length}
          </span>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setForm({ ...EMPTY_FORM });
            setShowForm(true);
          }}
          className="bg-[#314E6E] hover:bg-[#243b54] text-white gap-1"
        >
          <Plus className="h-4 w-4" />
          Add Notes
        </Button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-[#314E6E]" />
        </div>
      )}

      {/* Empty state */}
      {!loading && notes.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-[#5C5C57]">
          <NotebookPen className="h-10 w-10 mb-3 opacity-30" />
          <p className="text-sm font-medium">No meeting notes yet.</p>
          <p className="text-xs mt-1 opacity-70">
            Capture and extract insights from your meetings.
          </p>
        </div>
      )}

      {/* Notes list */}
      {!loading && notes.length > 0 && (
        <div className="space-y-3">
          {notes.map((note) => {
            const expanded = expandedId === note.id;
            const isExtracting = extracting[note.id];
            const hasExtracted =
              note.extracted_items && note.extracted_items.length > 0;
            const grouped = hasExtracted
              ? groupedItems(note.extracted_items!)
              : {};

            return (
              <div
                key={note.id}
                className="bg-[#FCFBF8] border border-[#D9D4CC] rounded-xl p-4"
              >
                {/* Card header */}
                <div className="flex items-start gap-3">
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() =>
                      setExpandedId(expanded ? null : note.id)
                    }
                  >
                    <p className="font-medium text-[#1E1E1C] text-sm leading-snug mb-1">
                      {note.title}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-[#5C5C57] flex-wrap">
                      {note.meeting_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(note.meeting_date).toLocaleDateString(
                            "en-AU",
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            }
                          )}
                        </span>
                      )}
                      {note.attendees && (
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {resolveAttendees(note.attendees)}
                        </span>
                      )}
                      {hasExtracted && (
                        <Badge
                          variant="outline"
                          className="text-[10px] bg-purple-50 text-purple-700 border-purple-200"
                        >
                          {note.extracted_items!.length} items extracted
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => extract(note.id)}
                      disabled={isExtracting}
                      className="h-7 px-2 text-xs border-[#D9D4CC] text-[#5C5C57] hover:text-[#314E6E] hover:border-[#314E6E]"
                    >
                      {isExtracting ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Sparkles className="h-3 w-3" />
                      )}
                      <span className="ml-1">Extract</span>
                    </Button>
                    <button
                      onClick={() => setDeleteConfirmId(note.id)}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-[#5C5C57] hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() =>
                        setExpandedId(expanded ? null : note.id)
                      }
                      className="p-1.5 rounded-lg hover:bg-[#F7F4EE] text-[#5C5C57] transition-colors"
                    >
                      {expanded ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Expanded content */}
                {expanded && (
                  <div className="mt-4 border-t border-[#D9D4CC] pt-4 space-y-4">
                    {/* Raw notes */}
                    {note.raw_notes && (
                      <div>
                        <p className="text-xs font-semibold text-[#314E6E] uppercase tracking-wide mb-2">
                          Raw Notes
                        </p>
                        <div className="bg-[#F7F4EE] rounded-lg p-3 border border-[#D9D4CC]">
                          <p className="text-sm text-[#5C5C57] whitespace-pre-wrap leading-relaxed">
                            {note.raw_notes}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Extracted items */}
                    {hasExtracted && (
                      <div>
                        <p className="text-xs font-semibold text-[#314E6E] uppercase tracking-wide mb-2">
                          Extracted Items
                        </p>
                        <div className="space-y-3">
                          {(
                            [
                              "requirement",
                              "action",
                              "decision",
                              "risk",
                            ] as const
                          )
                            .filter((type) => grouped[type]?.length)
                            .map((type) => (
                              <div key={type}>
                                <div className="flex items-center gap-1.5 mb-1.5">
                                  <Badge
                                    variant="outline"
                                    className={`text-xs capitalize ${ITEM_TYPE_COLORS[type]}`}
                                  >
                                    {ITEM_TYPE_LABELS[type]}s
                                  </Badge>
                                </div>
                                <ul className="space-y-1">
                                  {grouped[type].map((item, idx) => (
                                    <li
                                      key={idx}
                                      className="flex gap-2 text-sm text-[#1E1E1C]"
                                    >
                                      <span className="text-[#5C5C57] shrink-0 mt-0.5">
                                        •
                                      </span>
                                      <span>{item.text}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Prompt to extract if not yet done */}
                    {!hasExtracted && (
                      <div className="flex items-center gap-2 text-sm text-[#5C5C57]">
                        <Sparkles className="h-4 w-4 text-[#314E6E] opacity-60" />
                        <span>
                          Click{" "}
                          <span className="font-medium text-[#314E6E]">
                            Extract
                          </span>{" "}
                          to pull out requirements, actions, decisions, and risks
                          using AI.
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg bg-[#FCFBF8] border border-[#D9D4CC]">
          <DialogHeader>
            <DialogTitle className="text-[#1E1E1C]">Add Meeting Notes</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                Title <span className="text-red-500">*</span>
              </label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Stakeholder kickoff meeting"
                className="border-[#D9D4CC] bg-white text-[#1E1E1C]"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                  Date
                </label>
                <Input
                  type="date"
                  value={form.meeting_date}
                  onChange={(e) =>
                    setForm({ ...form, meeting_date: e.target.value })
                  }
                  className="border-[#D9D4CC] bg-white text-[#1E1E1C]"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#5C5C57] mb-1 block flex items-center gap-1">
                  <Users className="h-3 w-3" /> Attendees
                </label>
                {stakeholders.length > 0 ? (
                  <div className="space-y-2">
                    {/* Selected tags */}
                    {selectedIds.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {selectedIds.map(id => {
                          const s = stakeholders.find(x => x.id === id);
                          if (!s) return null;
                          return (
                            <span key={id} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30">
                              {s.name}
                              <button type="button" onClick={() => toggleAttendee(id)}><X className="h-2.5 w-2.5" /></button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {/* Stakeholder picker */}
                    <div className="flex flex-wrap gap-1 p-2 rounded border border-[#D9D4CC] bg-white min-h-[36px]">
                      {stakeholders.map(s => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => toggleAttendee(s.id)}
                          className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                            selectedIds.includes(s.id)
                              ? "bg-primary text-primary-foreground border-primary"
                              : "border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground/50"
                          }`}
                        >
                          {s.name} <span className="opacity-60">· {s.role}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Input
                    value={form.attendees}
                    onChange={(e) => setForm({ ...form, attendees: e.target.value })}
                    placeholder="Add stakeholders first, or type names"
                    className="border-[#D9D4CC] bg-white text-[#1E1E1C]"
                  />
                )}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                Notes
              </label>
              <Textarea
                value={form.raw_notes}
                onChange={(e) =>
                  setForm({ ...form, raw_notes: e.target.value })
                }
                placeholder="Paste or type your meeting notes here…"
                rows={8}
                className="border-[#D9D4CC] bg-white text-[#1E1E1C] resize-none"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowForm(false)}
              className="border-[#D9D4CC] text-[#5C5C57]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={saving || !form.title.trim()}
              className="bg-[#314E6E] hover:bg-[#243b54] text-white"
            >
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Save Notes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!deleteConfirmId}
        onOpenChange={() => setDeleteConfirmId(null)}
      >
        <DialogContent className="max-w-sm bg-[#FCFBF8] border border-[#D9D4CC]">
          <DialogHeader>
            <DialogTitle className="text-[#1E1E1C]">Delete Meeting Note?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#5C5C57] mt-1">
            This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteConfirmId(null)}
              className="border-[#D9D4CC] text-[#5C5C57]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => deleteConfirmId && remove(deleteConfirmId)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
